import { prettyDateTime } from "@app/date";
import { rollbackDeployment } from "@app/deploy";
import { deploymentKind, rollbackTargetFor } from "@app/deployment";
import { useDispatch, useLoader, useLoaderSuccess } from "@app/react";
import { activityByOperationUrl, operationDetailUrl } from "@app/routes";
import type { Deployment } from "@app/types";
import cn from "classnames";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Banner,
  BannerMessages,
  Button,
  CheckBox,
  DeploymentLabel,
  DockerImage,
  Form,
  GitRef,
  IconRollback,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeading,
  ModalWrapper,
  Pill,
} from "../index";

const pillVariantForStatus = (status: Deployment["status"]) => {
  if (status === "succeeded") return "success" as const;
  if (status === "failed" || status === "canceled") return "error" as const;
  return "progress" as const;
};

const pillLabelForStatus = (status: Deployment["status"]) => {
  if (status === "succeeded") return "Success";
  if (status === "failed") return "Fail";
  if (status === "canceled") return "Canceled";
  return status.charAt(0).toUpperCase() + status.slice(1);
};

/**
 * Display only: `rollbackTargetFor` always redeploys the deployment's recorded
 * image regardless of how this classifies it.
 */
function DeploymentRefDisplay({ deployment }: { deployment: Deployment }) {
  const kind = deploymentKind(deployment);

  if (kind === "image") {
    return <DockerImage image={deployment.dockerImage} digest="" />;
  }

  if (kind === "git" || kind === "companion-repo") {
    return (
      <GitRef
        gitRef={deployment.gitRef}
        commitSha={deployment.gitCommitSha}
        commitUrl={deployment.gitCommitUrl}
      />
    );
  }

  return <em className="text-black-500">No image or commit on record</em>;
}

function ActivityIdLink({ operationId }: { operationId: string }) {
  return (
    <div className="text-xs text-black-500">
      Activity for Operation{" "}
      <Link
        className="text-indigo hover:underline"
        to={activityByOperationUrl(operationId)}
        target="_blank"
        rel="noreferrer"
      >
        {operationId}
      </Link>
    </div>
  );
}

function DeploymentCardBody({ deployment }: { deployment: Deployment }) {
  return (
    <>
      <div className="flex flex-col gap-1 flex-1 min-w-0">
        <div className="min-w-0 break-all">
          <DeploymentRefDisplay deployment={deployment} />
        </div>
        <div className="text-xs text-black-500">
          Last deployed: {prettyDateTime(deployment.createdAt)}
        </div>
        <ActivityIdLink operationId={deployment.operationId} />
      </div>
      <Pill
        variant={pillVariantForStatus(deployment.status)}
        className="shrink-0"
      >
        {pillLabelForStatus(deployment.status)}
      </Pill>
    </>
  );
}

function DeploymentSummaryCard({
  deployment,
  label,
}: {
  deployment: Deployment;
  label: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      {label}
      <div className="border border-black-100 rounded-lg p-4 flex items-start gap-1 bg-white">
        <DeploymentCardBody deployment={deployment} />
      </div>
    </div>
  );
}

function DeploymentRadioCard({
  deployment,
  isSelected,
  onSelect,
}: {
  deployment: Deployment;
  isSelected: boolean;
  onSelect: () => void;
}) {
  return (
    <div
      className={cn(
        "border rounded-lg p-4 flex items-start gap-3 bg-white",
        isSelected ? "border-indigo ring-1 ring-indigo" : "border-black-100",
      )}
    >
      <input
        type="radio"
        name="rollback-target"
        className="mt-1 cursor-pointer"
        value={deployment.id}
        checked={isSelected}
        onChange={onSelect}
        aria-label={`Roll back to the deployment from ${prettyDateTime(
          deployment.createdAt,
        )}`}
      />
      <DeploymentCardBody deployment={deployment} />
    </div>
  );
}

export function RollbackDeploymentModal({
  isOpen,
  onClose,
  appName,
  currentDeployment,
  candidates,
  initialTargetId = "",
  renderEnvChanges,
}: {
  isOpen: boolean;
  onClose: () => void;
  appName: string;
  /** The app's most-recently-deployed deployment. */
  currentDeployment: Deployment;
  /**
   * Deployments the app can roll back to, newest first. The caller is
   * responsible for filtering (succeeded, has an image, not the current
   * deployment) and for capping the list. Only the newest is shown until the
   * user asks for more.
   */
  candidates: Deployment[];
  /** Preselect this deployment, e.g. the one the user was already viewing. */
  initialTargetId?: string;
  /**
   * Slot for the environment-variable diff. A render prop because the diff is
   * store-connected and this modal stays presentational so its tests need no
   * Provider.
   */
  renderEnvChanges?: (
    targetConfigurationId: string,
    onEmptyChange: (isEmpty: boolean) => void,
  ) => React.ReactNode;
}) {
  const [step, setStep] = useState<"select" | "confirm">("select");
  const [restoreEnv, setRestoreEnv] = useState(true);
  const [isEnvDiffEmpty, setIsEnvDiffEmpty] = useState(false);
  const [selectedId, setSelectedId] = useState(
    () =>
      candidates.find((d) => d.id === initialTargetId)?.id ??
      candidates[0]?.id ??
      "",
  );
  // Start expanded when the preselection isn't the newest candidate, which is
  // the only one shown collapsed.
  const [isExpanded, setIsExpanded] = useState(
    () => candidates.findIndex((d) => d.id === initialTargetId) > 0,
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const visibleCandidates = isExpanded ? candidates : candidates.slice(0, 1);
  const hiddenCount = candidates.length - visibleCandidates.length;
  const targetDeployment = candidates.find((d) => d.id === selectedId);
  const target = targetDeployment ? rollbackTargetFor(targetDeployment) : null;
  const canContinue = target !== null;
  const showRestoreEnv = !isEnvDiffEmpty;

  const action = rollbackDeployment({
    appId: targetDeployment?.appId ?? currentDeployment.appId,
    target,
    configurationId: targetDeployment?.configurationId ?? "",
    currentConfigurationId: currentDeployment.configurationId,
    restoreEnv,
  });
  const loader = useLoader(action);

  useLoaderSuccess(loader, () => {
    onClose();
    navigate(operationDetailUrl(loader.meta.opId));
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canContinue) return;
    if (step === "select") {
      setStep("confirm");
      return;
    }
    dispatch(action);
  };

  return isOpen ? (
    <ModalWrapper>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalHeading
          title="Rollback to previous deployment"
          icon={<IconRollback variant="sm" />}
          onClose={onClose}
        />
        <Form onSubmit={onSubmit}>
          <ModalBody className="flex flex-col gap-6">
            <BannerMessages {...loader} />
            <Banner variant="warning">
              <p className="text-sm">
                This only reverts app code and its configuration. Ensure
                dependencies are compatible.
              </p>
            </Banner>

            {step === "select" ? (
              <>
                <p className="text-sm text-black">
                  Rollback <strong className="font-semibold">{appName}</strong>{" "}
                  to a previous container image:
                </p>

                <DeploymentSummaryCard
                  deployment={currentDeployment}
                  label={
                    <DeploymentLabel variant="default">Current</DeploymentLabel>
                  }
                />

                {candidates.length === 0 ? (
                  <Banner variant="warning">
                    This app has no earlier successful deployment to roll back
                    to.
                  </Banner>
                ) : (
                  <div className="flex flex-col gap-1">
                    <DeploymentLabel variant="default">
                      Choose a deployment to restore
                    </DeploymentLabel>
                    <div className="flex flex-col gap-2">
                      {visibleCandidates.map((deployment) => (
                        <DeploymentRadioCard
                          key={deployment.id}
                          deployment={deployment}
                          isSelected={deployment.id === selectedId}
                          onSelect={() => setSelectedId(deployment.id)}
                        />
                      ))}
                    </div>
                    {hiddenCount > 0 ? (
                      <button
                        type="button"
                        className="text-left text-[11px] text-indigo hover:underline w-fit"
                        onClick={() => setIsExpanded(true)}
                      >
                        Choose a different deployment
                      </button>
                    ) : null}
                    {showRestoreEnv ? (
                      <div className="mt-2">
                        <CheckBox
                          checked={restoreEnv}
                          onChange={(e) =>
                            setRestoreEnv(e.currentTarget.checked)
                          }
                          label="Also restore environment variables from this deployment"
                          labelClassName="ml-2"
                        />
                      </div>
                    ) : null}
                    {restoreEnv && targetDeployment && renderEnvChanges ? (
                      <div
                        className="mt-2"
                        key={targetDeployment.configurationId}
                      >
                        {renderEnvChanges(
                          targetDeployment.configurationId,
                          setIsEnvDiffEmpty,
                        )}
                      </div>
                    ) : null}
                  </div>
                )}
              </>
            ) : null}

            {step === "confirm" && targetDeployment ? (
              <>
                <p className="text-sm text-black">
                  Rolling back{" "}
                  <strong className="font-semibold">{appName}</strong> will
                  start a new deployment using this image. Prerelease commands
                  and deploy hooks come from the selected image, so any
                  migrations it defines will run again.
                </p>

                <DeploymentSummaryCard
                  deployment={targetDeployment}
                  label={
                    <DeploymentLabel variant="success">
                      Selected to restore
                    </DeploymentLabel>
                  }
                />
              </>
            ) : null}
          </ModalBody>
          <ModalFooter
            actions={[
              step === "confirm" ? (
                <Button
                  key="back"
                  variant="white"
                  onClick={() => setStep("select")}
                  disabled={loader.isLoading}
                >
                  Back
                </Button>
              ) : (
                <Button
                  key="cancel"
                  variant="white"
                  onClick={onClose}
                  disabled={loader.isLoading}
                >
                  Cancel
                </Button>
              ),
              <Button
                key="submit"
                type="submit"
                isLoading={loader.isLoading}
                disabled={!canContinue}
              >
                {step === "select" ? "Continue" : "Rollback"}
              </Button>,
            ]}
          />
        </Form>
      </Modal>
    </ModalWrapper>
  ) : null;
}
